import { useEffect, useMemo } from 'react';

import { Dropdown } from '../../../../../common/Dropdown';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useGetAllTeams } from '../../../../../hooks/useGetAllTeams';
import { optionAll } from '../../../constants';

type TeamOption = {
  id: string;
  label: string;
  value: string;
};

interface TeamSelectorProps {
  selectedTeam: TeamOption | null;
  onTeamChange: (team: TeamOption | null) => void;
}

export function TeamSelector({
  selectedTeam,
  onTeamChange,
}: TeamSelectorProps): JSX.Element {
  const { data: teams, isLoading: isLoadingTeams } = useGetAllTeams();
  const { userData } = useAuth();

  const teamOptions = useMemo(
    () => [
      optionAll,
      ...(teams?.map((team) => ({
        id: team.id,
        label: team.name,
        value: team.id,
      })) ?? []),
    ],
    [teams],
  );

  const defaultTeam = useMemo(
    () =>
      teamOptions.find((team) => team.value === userData?.teamId) ||
      teamOptions[0],
    [teamOptions, userData?.teamId],
  );

  useEffect(() => {
    if (!selectedTeam && defaultTeam) {
      onTeamChange(defaultTeam);
    }
  }, [defaultTeam, selectedTeam, onTeamChange]);

  return (
    <Dropdown
      name="team"
      label="Team"
      labelPosition="inline"
      options={teamOptions}
      value={selectedTeam}
      variant="auto"
      onChange={onTeamChange}
      placeholder="Select team"
      isLoading={isLoadingTeams}
    />
  );
}
